import { SurveyBundle } from '../survey-bundle/survey-bundle.class';
import { SurveySession } from '../survey-session/survey-session.class';
import {
  SurveyListInstruction,
  SurveyListOption,
} from '@hyper-inferno/survey-elements-core';
import {
  ITransformationContext,
  QuestionHandler,
} from '../survey-engine/survey-engine.class';
import { shuffle, uniqBy } from 'lodash';
import { CustomScriptEngine } from './custom-script.engine';

export class ListInstructionProcessor {
  /**
   * Main entry point to transform instructions into a final list of options.
   */
  public static process(
    listId: string,
    ctx: ITransformationContext,
    partialData = {},
  ): SurveyListOption[] {
    const { bundle, session, handlers } = ctx;
    const listDef = bundle.getListByListIdQuery(listId);
    if (!listDef) return [];

    const instructions = listDef.instructions || [];
    let currentOptions: SurveyListOption[] = [];

    // Process each instruction in sequence
    session.jsn_responses = {
      ...session.jsn_responses,
      ...partialData,
    };
    try {
      for (const inst of instructions) {
        currentOptions = this.executeInstruction(
          inst,
          currentOptions,
          bundle,
          session,
          handlers,
          ctx,
        );
      }
    } catch (e) {
      throw Error(e as any);
    }

    // Apply global randomization if requested and not already handled by a specific instruction
    // if (
    //   randomizeRequested &&
    //   !instructions.some((i) => i.function === 'RANDOMIZE')
    // ) {
    //   currentOptions = shuffle(currentOptions);
    // }

    return currentOptions;
  }

  private static executeInstruction(
    inst: any,
    currentOptions: SurveyListOption[],
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    ctx: ITransformationContext,
  ): any[] {
    const func = inst.function;
    const targets = inst.option_targets || [];

    switch (func) {
      case 'ADD':
      case 'AIC':
      case 'ANC':
      case 'REMOVE': {
        const sourceItems = this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );

        if (func === 'REMOVE') {
          // Subtracts target items from the accumulated option list safely via ID comparison
          return sourceItems;
        } else {
          const position = this.resolvePosition(inst, currentOptions.length);
          // Insert items at the calculated position
          const newList = [...currentOptions];
          newList.splice(position, 0, ...sourceItems);
          return uniqBy(newList, 'id');
        }
      }

      case 'RIC': {
        return this.getSourceItems(
          inst,
          bundle,
          session,
          handlers,
          targets,
          ctx,
        );
      }

      case 'RANDOMIZE': {
        const specifyRange = inst.parameter_1 === 'true';
        if (!specifyRange) return shuffle(currentOptions);

        const start = Math.max(0, parseInt(inst.parameter_2 || '1') - 1);
        const end = Math.min(
          currentOptions.length,
          parseInt(inst.parameter_3 || String(currentOptions.length)),
        );

        const prefix = currentOptions.slice(0, start);
        const mid = shuffle(currentOptions.slice(start, end));
        const suffix = currentOptions.slice(end);
        return [...prefix, ...mid, ...suffix];
      }

      case 'LIST_LENGTH': {
        // parameter_1 is the limitValue
        const limit = parseInt(inst.parameters?.[0] || '0');

        const limitType = inst.type;

        if (isNaN(limit)) return currentOptions;

        switch (limitType) {
          case 'atmost':
            // Truncate to limit if length exceeds it
            return currentOptions.slice(0, limit);

          case 'exactly':
            // Force the list to be exactly this length (truncate if too many,
            // though usually 'exactly' implies validation elsewhere)
            return currentOptions.slice(0, limit);

          case 'atleast':
            // 'atleast' does not truncate the list.
            // It serves as a marker for validation logic later.
            return currentOptions;

          default:
            return currentOptions;
        }
      }

      case 'CUSTOM_SCRIPT': {
        const code = inst.parameters?.[0] || '';
        return CustomScriptEngine.execute(code, currentOptions, {
          bundle,
          session,
          handlers,
          ctx,
        });
      }

      default:
        return currentOptions;
    }
  }
  static getAnswer(
    function_question_id: number,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
  ): string[] {
    const element = bundle.fetchElementById(function_question_id);
    if (!element) return [];

    // Get the handler for that question type (Single Choice, Numeric, etc.)
    const handler = handlers[element.type];
    if (!handler || typeof handler.getValue !== 'function') {
      console.warn(`No getValue implementation for ${element.type}`);
      return [];
    }

    const elementAnswerObject = session.jsn_responses[element.id] ?? {};

    if (Object.keys(elementAnswerObject).length === 0) {
      throw new Error('AF Mode might be needed');
    }

    // Get user's answer via the specific handler
    const userValue: number[] = handler.getValue(
      element,
      elementAnswerObject,
    ) as number[];

    const mainList = bundle.getListOfQuestion(element.id);

    const selectedOptions = mainList?.options
      .filter((option) => userValue.includes(option.option_key))
      .map((option) => option.id);

    return selectedOptions ?? [];
  }

  /**
   * Resolves which items to pull based on 'type' (all, single item, range)
   */
  private static getSourceItems(
    inst: SurveyListInstruction,
    bundle: SurveyBundle,
    session: SurveySession,
    handlers: Record<string, QuestionHandler<any>>,
    targets: string[],
    ctx: ITransformationContext,
  ): SurveyListOption[] {
    let baseSet: SurveyListOption[] = [];

    // Identify Base Set
    if (['ADD', 'REMOVE'].includes(inst.function)) {
      if (!inst.function_list_id) {
        throw new Error('Error Identifying source list: No function list id');
      }

      const targetListId = inst.function_list_id;

      // Ensure cache registry exists on engine context
      if (!(ctx as any).listCache) {
        (ctx as any).listCache = {};
      }
      const cache = (ctx as any).listCache;

      // Break out cleanly if a circular dependency loop is discovered
      if (cache[targetListId] === '__COMPUTING__') {
        throw new Error(
          `Circular dependency detected processing list ID: ${targetListId}`,
        );
      }

      if (!cache[targetListId]) {
        cache[targetListId] = '__COMPUTING__';
        cache[targetListId] = ctx.listBuilding.getOptionsByListId(
          targetListId,
          ctx,
          false,
        );
      }

      baseSet = cache[targetListId];

      if (targets.length > 0) {
        if (inst.function === 'ADD') {
          // Only keep options that ARE in our targets list
          baseSet = baseSet.filter((opt) => targets.includes(opt.id));
        } else if (inst.function === 'REMOVE') {
          // Only keep options that ARE NOT in our targets list
          baseSet = baseSet.filter((opt) => !targets.includes(opt.id));
        }
      }
    } else if (
      inst.function === 'AIC' ||
      inst.function === 'ANC' ||
      inst.function === 'RIC'
    ) {
      // FIX: Changed '__COMPUTING__' back to 'RIC' above
      if (!inst.function_question_id) {
        throw new Error('Error Identifying source list: function_question_id');
      }

      let chosenIds = [] as string[];
      try {
        chosenIds =
          this.getAnswer(
            inst.function_question_id,
            bundle,
            session,
            handlers,
          ) || [];
      } catch (error) {
        throw new Error(error as any);
      }

      // Find referenced question element to extract its dynamic target list ID
      const element = bundle.fetchElementById(inst.function_question_id);
      const listId = element?.config?.['list_id'] || (element as any)?.list_id;

      if (listId) {
        if (!(ctx as any).listCache) {
          (ctx as any).listCache = {};
        }
        const cache = (ctx as any).listCache;

        if (cache[listId] === '__COMPUTING__') {
          throw new Error(
            `Circular dependency detected processing list ID: ${listId}`,
          );
        }

        if (!cache[listId]) {
          cache[listId] = '__COMPUTING__';
          cache[listId] = ctx.listBuilding.getOptionsByListId(
            listId,
            ctx,
            false,
          );
        }
        baseSet = cache[listId];
      } else {
        // Fallback to reading raw structural payload schema if properties layout aren't found
        const sourceList = bundle.getListOfQuestion(inst.function_question_id);
        baseSet = sourceList?.options || [];
      }

      // Apply answer rules
      if (inst.function === 'AIC') {
        baseSet = baseSet.filter((opt) => chosenIds.includes(opt.id));
      } else {
        baseSet = baseSet.filter((opt) => !chosenIds.includes(opt.id));
      }
    }

    // Apply Sub-Selection (The "Type" logic)
    const selectionType = inst.type; // "all", "single item", "range"
    if (selectionType === 'single item') {
      const idx = parseInt(inst.parameter_4!) - 1;
      return baseSet[idx] ? [baseSet[idx]] : [];
    } else if (selectionType === 'range') {
      const start = parseInt(inst.parameter_5!) - 1;
      const end = parseInt(inst.parameter_6!);
      return baseSet.slice(start, end);
    }

    return baseSet; // default to "all"
  }

  /**
   * Determines WHERE to insert items (parameter_1, parameter_2, parameter_3)
   */
  private static resolvePosition(inst: any, currentLength: number): number {
    const specifyPos = inst.parameter_1 === 'true';
    if (!specifyPos) return currentLength; // Default to end

    const posType = inst.parameter_2; // "top", "bottom", "custom position"
    if (posType === 'top') return 0;
    if (posType === 'bottom') return currentLength;
    if (posType === 'custom position') {
      const custom = parseInt(inst.parameter_3) - 1;
      return Math.max(0, Math.min(currentLength, custom));
    }

    return currentLength;
  }
}
